import FeatureLayer from '@arcgis/core/layers/FeatureLayer';
import Graphic from '@arcgis/core/Graphic';
import SpatialReference from '@arcgis/core/geometry/SpatialReference';
import Geometry from '@arcgis/core/geometry/Geometry';
import { StatisticDefinitionProperties } from '@arcgis/core/rest/support/StatisticDefinition';
import { QueryProperties } from '@arcgis/core/rest/support/Query';
import {
  RowCropLayers,
  PermPlantingCropLayers,
  SVFChipsViewMapServer
} from './services';

// ─── Types ────────────────────────────────────────────────────────────────────

/**
 * The kind of crop layer behind an ABBV_NAME entry. Drives which
 * fields/statistics/grouping the query uses.
 *
 * - `rowCrop`        — AGNW / CFF style (PlantedAcres, Yield, EnterpriseAbbr)
 * - `permPlanting`   — SVF/DFC blocks (Tree_Acres, Crop, Status)
 * - `permPlantingChips` — SVF CHIPS VIEW (Tree_Acres + USDALbsPerAcre yield)
 * - `simpleAcres`    — SHO style (only GIS_Acres + Crop grouping)
 */
export type AgronomyLayerKind =
  | 'rowCrop'
  | 'permPlanting'
  | 'permPlantingChips'
  | 'simpleAcres';

/**
 * A single entry in the agronomy layer mapping, containing the layer kind, feature layer reference, label, and optional groupBy field.
 */
export interface AgronomyLayerEntry {
  abbv: string;
  url: string;
  kind: AgronomyLayerKind;
  /** Optional friendly label for UI/debug */
  description?: string;
}

/** A single normalized crop summary row. */
export interface AgronomyCropSummary {
  abbv: string;
  crop: string;
  /** Most relevant acre count for this crop/abbv */
  totalAcres: number;
  /** Average yield (numeric) — semantics depend on yieldUOM */
  avgYield: number | null;
  /** Unit of measure for avgYield, e.g. 'BU/AC', 'LBS/AC' */
  yieldUOM: string | null;
  /** Optional per-row meta (status, enterprise abbr, etc.) */
  meta?: Record<string, unknown>;
}

/** A yield-history time-series point keyed by crop. */
export interface AgronomyYieldHistoryPoint {
  abbv: string;
  crop: string;
  /** Variety / cultivar within the crop (when the layer tracks it) */
  variety?: string | null;
  cropYear: number;
  avgYield: number | null;
  yieldUOM: string | null;
}

/** A planted-acreage time-series point keyed by crop. */
export interface AgronomyAcreHistoryPoint {
  abbv: string;
  crop: string;
  /** Variety / cultivar within the crop (when the layer tracks it) */
  variety?: string | null;
  cropYear: number;
  /** Planted acres for this crop in this crop year (null when not reported) */
  plantedAcres: number | null;
}

/**
 * "What's planted right now" — current-crop-year footprint by crop +
 * (when available) variety. No multi-year aggregates here. May include
 * `currentYearYield` for layers that record yield alongside the active
 * year's planting (rowCrop, permPlantingChips).
 */
export interface AgronomyCurrentPlanting {
  abbv: string;
  crop: string;
  variety?: string | null;
  /** Planted / GIS acres for the active crop year */
  acres: number;
  /** Harvested acres (CHIPS view only — subset of `acres` that yielded) */
  harvestAcres?: number | null;
  /** Status flag (perm-planting layers — Active / Non-Production / etc.) */
  status?: string | null;
  /** Enterprise abbreviation (rowCrop layers) */
  enterpriseAbbr?: string | null;
  /** Active-year yield if reported on the same row, else null */
  currentYearYield?: number | null;
  /** Unit of measure for `currentYearYield` */
  yieldUOM?: string | null;
  /** Crop year these acres represent */
  cropYear?: number;
}

/**
 * Per-variety multi-year roll-up — same metrics as `AgronomyCropPerformance`
 * but scoped to a single cultivar within the crop.
 */
export interface AgronomyVarietyPerformance {
  variety: string;
  yieldUOM: string | null;
  yearsTracked: number;
  firstYear: number | null;
  latestYear: number | null;
  avgYield: number | null;
  peakYield: number | null;
  latestYield: number | null;
  history: AgronomyYieldHistoryPoint[];
  acreHistory: AgronomyAcreHistoryPoint[];
  latestAcres: number | null;
  /** Sum of `plantedAcres` across every year in the history window */
  totalAcresOverPeriod: number;
}

/**
 * "What we have planted along with yield" — multi-year roll-up for one
 * crop. Aggregated from `AgronomyYieldHistoryPoint[]` so a single crop
 * gets one summary row regardless of how many years are present.
 */
export interface AgronomyCropPerformance {
  abbv: string;
  crop: string;
  yieldUOM: string | null;
  /** Number of years that contributed a non-null avg yield */
  yearsTracked: number;
  /** Earliest tracked year (or null when no data) */
  firstYear: number | null;
  /** Latest tracked year (or null when no data) */
  latestYear: number | null;
  /** Simple mean of per-year average yields across the window */
  avgYield: number | null;
  /** Highest per-year average yield in the window */
  peakYield: number | null;
  /** Per-year average yield in the latest tracked year */
  latestYield: number | null;
  /** Sorted (year asc) yield history points for this crop */
  history: AgronomyYieldHistoryPoint[];
  /** Sorted (year asc) planted-acreage history points for this crop */
  acreHistory: AgronomyAcreHistoryPoint[];
  /** Latest reported planted acres */
  latestAcres: number | null;
  /** Sum of `plantedAcres` across every year in the history window */
  totalAcresOverPeriod: number;
  /** Acreage difference between earliest and latest tracked year (null if <2 points) */
  acreChange: number | null;
  /** Per-variety detail rolls (when the underlying layer tracks varieties) */
  varieties: AgronomyVarietyPerformance[];
}

/**
 * Query options for agronomy lookups, controlling crop year, history window, and optional spatial filters.
 *
 * Geometry and spatialRelationship are optional — when omitted, the lookup pulls all features for the registered ABBV.
 *
 * @param cropYear - Target crop year for current-year aggregates; defaults to the current calendar year.
 * @param yieldHistoryYears - How many years of history to pull for yield trends; defaults to 10.
 * @param geometry - Optional spatial filter for state-boundary-crossing features (e.g. AGNW).
 * @param spatialReference - Spatial reference for outbound queries.
 * @param spatialRelationship - Spatial relationship when geometry is provided; defaults to 'intersects'.
 */
export interface AgronomyQueryOptions {
  /** Target crop year for current-year aggregates (defaults to current year) */
  cropYear?: number;
  /** How many years of history to pull for yield trend (defaults to 10) */
  yieldHistoryYears?: number;
  /**
   * Optional spatial filter — useful for layers whose features cross state
   * boundaries (e.g. AGNW spans WA/OR). When omitted, the lookup pulls all
   * features for the given ABBV.
   */
  geometry?: Geometry;
  /** Spatial reference for outbound queries */
  spatialReference?: SpatialReference;
  /**
   * Spatial relationship to use when `geometry` is provided.
   * Defaults to 'intersects' to be tolerant of cross-boundary features.
   */
  spatialRelationship?: QueryProperties['spatialRelationship'];
}

// ─── Registry ─────────────────────────────────────────────────────────────────

/**
 * Registry keyed by Property Boundary `ABBV_NAME`.
 *
 * Add entries here as new operations come online. Lookups against
 * unknown ABBVs return `null` and are skipped by the aggregator.
 */
export const agronomyLayerRegistry: Record<string, AgronomyLayerEntry> = {
  AGNW: {
    abbv: 'AGNW',
    url: RowCropLayers.AGNW.url,
    kind: 'rowCrop',
    description: ''
  },
  CFF: {
    abbv: 'CFF',
    url: RowCropLayers.CFF.url,
    kind: 'rowCrop',
    description: 'Canyon Falls Farm — row crops'
  },
  SVF: {
    abbv: 'SVF',
    url: SVFChipsViewMapServer.url,
    kind: 'permPlantingChips',
    description: 'perm planting (CHIPS view)'
  },
  DFC: {
    abbv: 'DFC',
    url: PermPlantingCropLayers.DFC.url,
    kind: 'permPlanting',
    description: 'B — perm planting'
  },
  SHO: {
    abbv: 'SHO',
    url: PermPlantingCropLayers.SHO.url,
    kind: 'simpleAcres',
    description: 'Sherman Orchards — simple block acres'
  },
  ARC: {
    abbv: 'ARC',
    url: PermPlantingCropLayers.ARC.url,
    kind: 'permPlanting',
    description: 'Chile (ARC) — olive permanent plantings'
  }
};

/**
 * Lookup a registry entry by ABBV_NAME. Returns null when unknown so callers
 * can simply filter/skip.
 */
export const getAgronomyLayerEntry = (abbv: string | null | undefined): AgronomyLayerEntry | null => {
  if (!abbv) return null;
  return agronomyLayerRegistry[abbv] ?? null;
};

// ─── Query specs per kind ─────────────────────────────────────────────────────

const rowCropGroupBy = ['EnterpriseAbbr', 'EnterpriseDescription', 'VarietyAbbr','VarietyDescription', 'YieldUOM'];

const rowCropAcreStats: StatisticDefinitionProperties[] = [
  { onStatisticField: 'PlantedAcres', outStatisticFieldName: 'total_planted_acres', statisticType: 'sum' },
  { onStatisticField: 'UsableAcres', outStatisticFieldName: 'total_usable_acres', statisticType: 'sum' },
  { onStatisticField: 'Yield', outStatisticFieldName: 'avg_yield', statisticType: 'avg' }
];
const rowCropYieldStats: StatisticDefinitionProperties[] = [
  { onStatisticField: 'Yield', outStatisticFieldName: 'avg_yield', statisticType: 'avg' }
];

const permPlantingGroupBy = ['Crop', 'Status'];
const permPlantingStats: StatisticDefinitionProperties[] = [
  { onStatisticField: 'Tree_Acres', outStatisticFieldName: 'total_planted_acres', statisticType: 'sum' }
];

// CHIPS view supports CropYear + USDALbsPerAcre, so we can build a real history
const permPlantingChipsGroupBy = ['Crop', 'Varieties'];
const permPlantingChipsCurrentStats: StatisticDefinitionProperties[] = [
  { onStatisticField: 'Tree_Acres', outStatisticFieldName: 'total_planted_acres', statisticType: 'sum' },
  { onStatisticField: 'HarvestAcres', outStatisticFieldName: 'total_harvest_acres', statisticType: 'sum' },
  { onStatisticField: 'USDALbsPerAcre', outStatisticFieldName: 'avg_yield', statisticType: 'avg' }
];
const permPlantingChipsYieldStats: StatisticDefinitionProperties[] = [
  { onStatisticField: 'USDALbsPerAcre', outStatisticFieldName: 'avg_yield', statisticType: 'avg' }
];

// ─── Acreage-history stats ────────────────────────────────────────────────────
// (Year-over-year planted acres per crop. Same time window as yield history.)

const rowCropAcreHistoryStats: StatisticDefinitionProperties[] = [
  { onStatisticField: 'PlantedAcres', outStatisticFieldName: 'total_planted_acres', statisticType: 'sum' }
];
const permPlantingChipsAcreHistoryStats: StatisticDefinitionProperties[] = [
  { onStatisticField: 'Tree_Acres', outStatisticFieldName: 'total_planted_acres', statisticType: 'sum' }
];

const simpleAcresGroupBy = ['Crop'];
const simpleAcresStats: StatisticDefinitionProperties[] = [
  { onStatisticField: 'GIS_Acres', outStatisticFieldName: 'total_planted_acres', statisticType: 'sum' }
];

// ─── Query helpers ────────────────────────────────────────────────────────────

const applySpatial = (
  query: Partial<QueryProperties>,
  options: AgronomyQueryOptions
): Partial<QueryProperties> => {
  if (!options.geometry) return query;
  return {
    ...query,
    geometry: options.geometry,
    spatialRelationship: options.spatialRelationship ?? 'intersects'
  };
};

const buildCurrentQuery = (
  entry: AgronomyLayerEntry,
  options: AgronomyQueryOptions
): Partial<QueryProperties> => {
  const cropYear = options.cropYear ?? new Date().getFullYear();
  switch (entry.kind) {
    case 'rowCrop':
      return applySpatial(
        {
          where: `CropYear=${cropYear}`,
          groupByFieldsForStatistics: rowCropGroupBy,
          outStatistics: rowCropAcreStats
        },
        options
      );
    case 'permPlanting':
      return applySpatial(
        {
          where: `Status <> 'Non-Production Areas' AND Crop not in ('Non-Production Areas','None','Leaseable Row Crop Areas')`,
          groupByFieldsForStatistics: permPlantingGroupBy,
          outStatistics: permPlantingStats
        },
        options
      );
    case 'permPlantingChips':
      return applySpatial(
        {
          where: `CropYear=${cropYear-1} AND Status <> 'Non-Production Areas' AND Crop not in ('Non-Production Areas','None','Leaseable Row Crop Areas')`,
          groupByFieldsForStatistics: permPlantingChipsGroupBy,
          outStatistics: permPlantingChipsCurrentStats
        },
        options
      );
    case 'simpleAcres':
      return applySpatial(
        {
          where: '1=1',
          groupByFieldsForStatistics: simpleAcresGroupBy,
          outStatistics: simpleAcresStats
        },
        options
      );
  }
};

const buildHistoryQuery = (
  entry: AgronomyLayerEntry,
  options: AgronomyQueryOptions
): Partial<QueryProperties> | null => {
  const cropYear = options.cropYear ?? new Date().getFullYear();
  const years = options.yieldHistoryYears ?? 20;
  const startYear = cropYear - years;
  switch (entry.kind) {
    case 'rowCrop':
      return applySpatial(
        {
          where: `CropYear>${startYear} AND CropYear<=${cropYear} AND Yield IS NOT NULL`,
          groupByFieldsForStatistics: [...rowCropGroupBy, 'CropYear'],
          outStatistics: rowCropYieldStats,
          orderByFields: ['CropYear']
        },
        options
      );
    case 'permPlantingChips':
      return applySpatial(
        {
          where: `CropYear>${startYear} AND CropYear<=${cropYear} AND USDALbsPerAcre is not null`,
          groupByFieldsForStatistics: ['Crop', 'Varieties', 'CropYear'],
          outStatistics: permPlantingChipsYieldStats,
          orderByFields: ['CropYear']
        },
        options
      );
    // permPlanting / simpleAcres lack a CropYear field — no history available
    case 'permPlanting':
    case 'simpleAcres':
      return null;
  }
};

/**
 * Year-over-year planted-acres query (for the acreage trend chart). Same
 * time window and CropYear-aware kinds as `buildHistoryQuery`.
 */
const buildAcreHistoryQuery = (
  entry: AgronomyLayerEntry,
  options: AgronomyQueryOptions
): Partial<QueryProperties> | null => {
  const cropYear = options.cropYear ?? new Date().getFullYear();
  const years = options.yieldHistoryYears ?? 10;
  const startYear = cropYear - years;
  switch (entry.kind) {
    case 'rowCrop':
      return applySpatial(
        {
          where: `CropYear>${startYear} AND CropYear<=${cropYear}`,
          groupByFieldsForStatistics: [
            'EnterpriseDescription',
            'EnterpriseAbbr',
            'VarietyDescription',
            'CropYear'
          ],
          outStatistics: rowCropAcreHistoryStats,
          orderByFields: ['CropYear']
        },
        options
      );
    case 'permPlantingChips':
      return applySpatial(
        {
          where: `CropYear>${startYear} AND CropYear<=${cropYear}`,
          groupByFieldsForStatistics: ['Crop', 'Varieties', 'CropYear'],
          outStatistics: permPlantingChipsAcreHistoryStats,
          orderByFields: ['CropYear']
        },
        options
      );
    case 'permPlanting':
    case 'simpleAcres':
      return null;
  }
};

const normalizeCurrentRow = (
  entry: AgronomyLayerEntry,
  feature: Graphic
): AgronomyCropSummary => {
  const a = feature.attributes ?? {};
  switch (entry.kind) {
    case 'rowCrop':
      return {
        abbv: entry.abbv,
        crop: a.EnterpriseDescription ?? a.EnterpriseAbbr ?? 'Unknown',
        totalAcres: a.total_planted_acres ?? a.total_usable_acres ?? 0,
        avgYield: a.avg_yield ?? null,
        yieldUOM: a.YieldUOM ?? null,
        meta: { enterpriseAbbr: a.EnterpriseAbbr }
      };
    case 'permPlantingChips':
      return {
        abbv: entry.abbv,
        crop: a.Crop ?? 'Unknown',
        totalAcres: a.total_planted_acres ?? 0,
        avgYield: a.avg_yield ?? null,
        // USDALbsPerAcre is lbs/acre by definition
        yieldUOM: a.avg_yield != null ? 'LBS/AC' : null,
        meta: { status: a.Status, harvestAcres: a.total_harvest_acres }
      };
    case 'permPlanting':
      return {
        abbv: entry.abbv,
        crop: a.Crop ?? 'Unknown',
        totalAcres: a.total_planted_acres ?? 0,
        avgYield: null,
        yieldUOM: null,
        meta: { status: a.Status }
      };
    case 'simpleAcres':
      return {
        abbv: entry.abbv,
        crop: a.Crop ?? 'Unknown',
        totalAcres: a.total_planted_acres ?? 0,
        avgYield: null,
        yieldUOM: null
      };
  }
};

const normalizeHistoryRow = (
  entry: AgronomyLayerEntry,
  feature: Graphic
): AgronomyYieldHistoryPoint | null => {
  const a = feature.attributes ?? {};
  if (a.CropYear == null) return null;
  switch (entry.kind) {
    case 'rowCrop':
      return {
        abbv: entry.abbv,
        crop: a.EnterpriseDescription ?? a.EnterpriseAbbr ?? 'Unknown',
        variety: a.VarietyDescription ?? null,
        cropYear: a.CropYear,
        avgYield: a.avg_yield ?? null,
        yieldUOM: a.YieldUOM ?? null
      };
    case 'permPlantingChips':
      return {
        abbv: entry.abbv,
        crop: a.Crop ?? 'Unknown',
        variety: a.Varieties ?? null,
        cropYear: a.CropYear,
        avgYield: a.avg_yield ?? null,
        yieldUOM: a.avg_yield != null ? 'LBS/AC' : null
      };
    default:
      return null;
  }
};

const normalizeAcreHistoryRow = (
  entry: AgronomyLayerEntry,
  feature: Graphic
): AgronomyAcreHistoryPoint | null => {
  const a = feature.attributes ?? {};
  if (a.CropYear == null) return null;
  switch (entry.kind) {
    case 'rowCrop':
      return {
        abbv: entry.abbv,
        crop: a.EnterpriseDescription ?? a.EnterpriseAbbr ?? 'Unknown',
        variety: a.VarietyDescription ?? null,
        cropYear: a.CropYear,
        plantedAcres: a.total_planted_acres ?? null
      };
    case 'permPlantingChips':
      return {
        abbv: entry.abbv,
        crop: a.Crop ?? 'Unknown',
        variety: a.Varieties ?? null,
        cropYear: a.CropYear,
        plantedAcres: a.total_planted_acres ?? null
      };
    default:
      return null;
  }
};

/** Project a current-row attribute set to the explicit "now" shape. */
const normalizeCurrentPlanting = (
  entry: AgronomyLayerEntry,
  feature: Graphic,
  cropYear: number
): AgronomyCurrentPlanting => {
  const a = feature.attributes ?? {};
  switch (entry.kind) {
    case 'rowCrop':
      return {
        abbv: entry.abbv,
        crop: a.EnterpriseDescription ?? a.EnterpriseAbbr ?? 'Unknown',
        variety: a.VarietyDescription ?? null,
        acres: a.total_planted_acres ?? a.total_usable_acres ?? 0,
        currentYearYield: a.avg_yield ?? null,
        yieldUOM: a.YieldUOM ?? null,
        enterpriseAbbr: a.EnterpriseAbbr ?? null,
        cropYear
      };
    case 'permPlantingChips':
      return {
        abbv: entry.abbv,
        crop: a.Crop ?? 'Unknown',
        variety: a.Varieties ?? null,
        acres: a.total_planted_acres ?? 0,
        harvestAcres: a.total_harvest_acres ?? null,
        currentYearYield: a.avg_yield ?? null,
        yieldUOM: a.avg_yield != null ? 'LBS/AC' : null,
        cropYear
      };
    case 'permPlanting':
      return {
        abbv: entry.abbv,
        crop: a.Crop ?? 'Unknown',
        acres: a.total_planted_acres ?? 0,
        status: a.Status ?? null,
        currentYearYield: null,
        yieldUOM: null
      };
    case 'simpleAcres':
      return {
        abbv: entry.abbv,
        crop: a.Crop ?? 'Unknown',
        acres: a.total_planted_acres ?? 0,
        currentYearYield: null,
        yieldUOM: null
      };
  }
};

interface PerformanceMetrics {
  yieldUOM: string | null;
  yearsTracked: number;
  firstYear: number | null;
  latestYear: number | null;
  avgYield: number | null;
  peakYield: number | null;
  latestYield: number | null;
  history: AgronomyYieldHistoryPoint[];
  acreHistory: AgronomyAcreHistoryPoint[];
  latestAcres: number | null;
  totalAcresOverPeriod: number;
  acreChange: number | null;
}

const computePerformanceMetrics = (
  yieldPoints: ReadonlyArray<AgronomyYieldHistoryPoint>,
  acrePoints: ReadonlyArray<AgronomyAcreHistoryPoint>
): PerformanceMetrics => {
  // Detect identity (abbv/crop/uom) from raw input — used when synthesizing
  // yearly-aggregated points for charts.
  const sample = yieldPoints[0] ?? acrePoints[0];
  const abbv = sample?.abbv ?? '';
  const cropName = sample?.crop ?? '';
  const uom =
    yieldPoints.find((p) => p.yieldUOM && p.avgYield != null)?.yieldUOM ?? null;

  // ── Aggregate yield by crop-year (mean across varieties in that year) ──
  const yieldByYear = new Map<number, number[]>();
  for (const p of yieldPoints) {
    if (p.avgYield == null) continue;
    const list = yieldByYear.get(p.cropYear) ?? [];
    list.push(p.avgYield);
    yieldByYear.set(p.cropYear, list);
  }
  const yearlyYieldHistory: AgronomyYieldHistoryPoint[] = Array.from(yieldByYear.entries())
    .map(([cropYear, vals]) => ({
      abbv,
      crop: cropName,
      cropYear,
      avgYield: vals.reduce((s, v) => s + v, 0) / vals.length,
      yieldUOM: uom
    }))
    .sort((a, b) => a.cropYear - b.cropYear);

  // ── Aggregate planted-acres by crop-year (sum across varieties) ────────
  const acreByYear = new Map<number, number>();
  for (const p of acrePoints) {
    if (p.plantedAcres == null) continue;
    acreByYear.set(p.cropYear, (acreByYear.get(p.cropYear) ?? 0) + p.plantedAcres);
  }
  const yearlyAcreHistory: AgronomyAcreHistoryPoint[] = Array.from(acreByYear.entries())
    .map(([cropYear, plantedAcres]) => ({
      abbv,
      crop: cropName,
      cropYear,
      plantedAcres
    }))
    .sort((a, b) => a.cropYear - b.cropYear);

  // ── Derive metrics from the per-year aggregates ────────────────────────
  const yields = yearlyYieldHistory.map((p) => p.avgYield as number);
  const first = yearlyYieldHistory[0];
  const last = yearlyYieldHistory[yearlyYieldHistory.length - 1];
  const acreFirst = yearlyAcreHistory[0];
  const acreLast = yearlyAcreHistory[yearlyAcreHistory.length - 1];
  const totalAcresOverPeriod = yearlyAcreHistory.reduce(
    (s, p) => s + (p.plantedAcres ?? 0),
    0
  );

  return {
    yieldUOM: uom,
    yearsTracked: yearlyYieldHistory.length,
    firstYear: first?.cropYear ?? acreFirst?.cropYear ?? null,
    latestYear: last?.cropYear ?? acreLast?.cropYear ?? null,
    avgYield: yields.length > 0 ? yields.reduce((s, v) => s + v, 0) / yields.length : null,
    peakYield: yields.length > 0 ? Math.max(...yields) : null,
    latestYield: last?.avgYield ?? null,
    history: yearlyYieldHistory,
    acreHistory: yearlyAcreHistory,
    latestAcres: acreLast?.plantedAcres ?? null,
    totalAcresOverPeriod,
    acreChange:
      acreFirst && acreLast && acreFirst !== acreLast
        ? (acreLast.plantedAcres ?? 0) - (acreFirst.plantedAcres ?? 0)
        : null
  };
};

/**
 * Roll a flat history list up into one performance row per crop, with
 * per-variety sub-rolls. Pass results from `getAgronomyYieldHistoryForAbbv`
 * + `getAgronomyAcreHistoryForAbbv` (or the aggregated equivalents).
 */
export const summarizeCropPerformance = (
  abbv: string,
  history: ReadonlyArray<AgronomyYieldHistoryPoint>,
  acreHistory: ReadonlyArray<AgronomyAcreHistoryPoint> = []
): AgronomyCropPerformance[] => {
  // Crop-level grouping
  const yieldByCrop = new Map<string, AgronomyYieldHistoryPoint[]>();
  for (const point of history) {
    const list = yieldByCrop.get(point.crop) ?? [];
    list.push(point);
    yieldByCrop.set(point.crop, list);
  }
  const acreByCrop = new Map<string, AgronomyAcreHistoryPoint[]>();
  for (const point of acreHistory) {
    const list = acreByCrop.get(point.crop) ?? [];
    list.push(point);
    acreByCrop.set(point.crop, list);
  }
  const allCrops = new Set<string>([...yieldByCrop.keys(), ...acreByCrop.keys()]);

  const out: AgronomyCropPerformance[] = [];
  for (const crop of allCrops) {
    const yieldPoints = yieldByCrop.get(crop) ?? [];
    const acrePoints = acreByCrop.get(crop) ?? [];

    // Variety-level grouping within this crop
    const varietyKeys = new Set<string>();
    for (const p of yieldPoints) {
      if (p.variety) varietyKeys.add(p.variety);
    }
    for (const p of acrePoints) {
      if (p.variety) varietyKeys.add(p.variety);
    }
    const varieties: AgronomyVarietyPerformance[] = [];
    for (const variety of varietyKeys) {
      const vYield = yieldPoints.filter((p) => p.variety === variety);
      const vAcres = acrePoints.filter((p) => p.variety === variety);
      const m = computePerformanceMetrics(vYield, vAcres);
      varieties.push({
        variety,
        yieldUOM: m.yieldUOM,
        yearsTracked: m.yearsTracked,
        firstYear: m.firstYear,
        latestYear: m.latestYear,
        avgYield: m.avgYield,
        peakYield: m.peakYield,
        latestYield: m.latestYield,
        history: m.history,
        acreHistory: m.acreHistory,
        latestAcres: m.latestAcres,
        totalAcresOverPeriod: m.totalAcresOverPeriod
      });
    }
    varieties.sort(
      (a, b) =>
        b.totalAcresOverPeriod - a.totalAcresOverPeriod ||
        (b.latestAcres ?? 0) - (a.latestAcres ?? 0) ||
        a.variety.localeCompare(b.variety)
    );

    // Crop-level metrics use ALL points (variety-aware metrics live on `varieties`)
    const m = computePerformanceMetrics(yieldPoints, acrePoints);

    out.push({
      abbv,
      crop,
      yieldUOM: m.yieldUOM,
      yearsTracked: m.yearsTracked,
      firstYear: m.firstYear,
      latestYear: m.latestYear,
      avgYield: m.avgYield,
      peakYield: m.peakYield,
      latestYield: m.latestYield,
      history: m.history,
      acreHistory: m.acreHistory,
      latestAcres: m.latestAcres,
      totalAcresOverPeriod: m.totalAcresOverPeriod,
      acreChange: m.acreChange,
      varieties
    });
  }
  // Sort by total acres planted over the period (desc), then by crop name
  out.sort(
    (a, b) =>
      b.totalAcresOverPeriod - a.totalAcresOverPeriod ||
      a.crop.localeCompare(b.crop)
  );
  return out;
};

// ─── Public API ───────────────────────────────────────────────────────────────

const buildLayer = (entry: AgronomyLayerEntry, options: AgronomyQueryOptions) =>
  new FeatureLayer({
    url: entry.url,
    spatialReference: options.spatialReference,
    outFields: ['*']
  });

/**
 * Query a single ABBV_NAME's agronomy layer for current-year crop summary.
 * Returns null when the ABBV is not registered or has no data.
 */
export const getAgronomySummaryForAbbv = async (
  abbv: string,
  options: AgronomyQueryOptions = {}
): Promise<AgronomyCropSummary[] | null> => {
  const entry = getAgronomyLayerEntry(abbv);
  if (!entry) return null;

  const layer = buildLayer(entry, options);
  const result = await layer.queryFeatures(buildCurrentQuery(entry, options));
  return result.features.map((f) => normalizeCurrentRow(entry, f));
};

/**
 * "What's planted now" — query the current-year footprint for one ABBV.
 * Returns null when the ABBV isn't registered.
 *
 * Same underlying query as `getAgronomySummaryForAbbv`, but typed to the
 * focused `AgronomyCurrentPlanting` shape (no multi-year fields).
 */
export const getCurrentPlantingsForAbbv = async (
  abbv: string,
  options: AgronomyQueryOptions = {}
): Promise<AgronomyCurrentPlanting[] | null> => {
  const entry = getAgronomyLayerEntry(abbv);
  if (!entry) return null;
  const cropYear = options.cropYear ?? new Date().getFullYear();
  const layer = buildLayer(entry, options);
  const result = await layer.queryFeatures(buildCurrentQuery(entry, options));
  return result.features.map((f) => normalizeCurrentPlanting(entry, f, cropYear));
};

/**
 * Query yield history (year-over-year avg yield) for a single ABBV_NAME.
 * Returns null when the ABBV is unknown or its layer kind has no time series.
 */
export const getAgronomyYieldHistoryForAbbv = async (
  abbv: string,
  options: AgronomyQueryOptions = {}
): Promise<AgronomyYieldHistoryPoint[] | null> => {
  const entry = getAgronomyLayerEntry(abbv);
  if (!entry) return null;
  const query = buildHistoryQuery(entry, options);
  if (!query) return null;

  const layer = buildLayer(entry, options);
  const result = await layer.queryFeatures(query);
  return result.features
    .map((f) => normalizeHistoryRow(entry, f))
    .filter((r): r is AgronomyYieldHistoryPoint => r !== null);
};

/**
 * Query planted-acreage history (year-over-year sum of planted acres per
 * crop) for a single ABBV_NAME. Returns null when the ABBV is unknown or
 * its layer kind has no CropYear field.
 */
export const getAgronomyAcreHistoryForAbbv = async (
  abbv: string,
  options: AgronomyQueryOptions = {}
): Promise<AgronomyAcreHistoryPoint[] | null> => {
  const entry = getAgronomyLayerEntry(abbv);
  if (!entry) return null;
  const query = buildAcreHistoryQuery(entry, options);
  if (!query) return null;

  const layer = buildLayer(entry, options);
  const result = await layer.queryFeatures(query);
  return result.features
    .map((f) => normalizeAcreHistoryRow(entry, f))
    .filter((r): r is AgronomyAcreHistoryPoint => r !== null);
};

/**
 * Aggregated query result containing per-ABBV and flat summaries for current plantings, yield/acre history, and crop performance.
 *
 * @param byAbbv - Per-ABBV current-year summaries (legacy combined shape).
 * @param summaries - Flat current-year summary across every queried ABBV.
 * @param currentPlantingsByAbbv - Per-ABBV current plantings (focused current-year footprint).
 * @param currentPlantings - Flat current plantings across every queried ABBV.
 * @param yieldHistoryByAbbv - Per-ABBV yield history (only for ABBVs whose layer kind supports it).
 * @param acreHistoryByAbbv - Per-ABBV planted-acreage history (only for ABBVs whose layer kind supports it).
 * @param cropPerformanceByAbbv - Per-ABBV crop performance roll-ups (multi-year yield aggregates).
 * @param cropPerformance - Flat crop-performance rows across every queried ABBV.
 * @param unknownAbbvs - ABBVs that were requested but have no registry entry (skipped).
 */
export interface AggregatedAgronomyResult {
  /** Per-ABBV current-year summaries (legacy combined shape) */
  byAbbv: Record<string, AgronomyCropSummary[]>;
  /** Flat current-year summary across every queried ABBV (legacy) */
  summaries: AgronomyCropSummary[];
  /** Per-ABBV "what's planted now" — focused current-year footprint */
  currentPlantingsByAbbv: Record<string, AgronomyCurrentPlanting[]>;
  /** Flat current plantings across every queried ABBV */
  currentPlantings: AgronomyCurrentPlanting[];
  /** Per-ABBV yield history (only ABBVs whose layer kind supports it) */
  yieldHistoryByAbbv: Record<string, AgronomyYieldHistoryPoint[]>;
  /** Per-ABBV planted-acreage history (only ABBVs whose layer kind supports it) */
  acreHistoryByAbbv: Record<string, AgronomyAcreHistoryPoint[]>;
  /** Per-ABBV crop performance roll-ups (multi-year yield aggregates) */
  cropPerformanceByAbbv: Record<string, AgronomyCropPerformance[]>;
  /** Flat crop-performance rows across every queried ABBV */
  cropPerformance: AgronomyCropPerformance[];
  /** ABBVs that were requested but have no registry entry (skipped) */
  unknownAbbvs: string[];
}

/**
 * Aggregate query for many ABBV_NAMEs at once. Unknown ABBVs are skipped.
 *
 * @param abbvNames list of ABBV_NAME values, e.g. from a property-boundary
 *                  query against a state polygon
 * @param options   shared query options applied to every layer hit
 */
export const getAgronomyForAbbvs = async (
  abbvNames: ReadonlyArray<string | null | undefined>,
  options: AgronomyQueryOptions = {}
): Promise<AggregatedAgronomyResult> => {
  const unique = Array.from(new Set(abbvNames.filter((a): a is string => !!a)));
  const known = unique.map((abbv) => ({ abbv, entry: getAgronomyLayerEntry(abbv) }));
  const unknownAbbvs = known.filter((k) => !k.entry).map((k) => k.abbv);

  const tasks = known
    .filter((k): k is { abbv: string; entry: AgronomyLayerEntry } => !!k.entry)
    .map(async ({ abbv, entry }) => {
      const [summary, plantings, history, acreHistory] = await Promise.all([
        getAgronomySummaryForAbbv(abbv, options),
        getCurrentPlantingsForAbbv(abbv, options),
        getAgronomyYieldHistoryForAbbv(abbv, options),
        getAgronomyAcreHistoryForAbbv(abbv, options)
      ]);
      return {
        abbv,
        entry,
        summary: summary ?? [],
        plantings: plantings ?? [],
        history,
        acreHistory
      };
    });

  const settled = await Promise.all(tasks);

  const byAbbv: Record<string, AgronomyCropSummary[]> = {};
  const currentPlantingsByAbbv: Record<string, AgronomyCurrentPlanting[]> = {};
  const yieldHistoryByAbbv: Record<string, AgronomyYieldHistoryPoint[]> = {};
  const acreHistoryByAbbv: Record<string, AgronomyAcreHistoryPoint[]> = {};
  const cropPerformanceByAbbv: Record<string, AgronomyCropPerformance[]> = {};
  const summaries: AgronomyCropSummary[] = [];
  const currentPlantings: AgronomyCurrentPlanting[] = [];
  const cropPerformance: AgronomyCropPerformance[] = [];

  for (const { abbv, summary, plantings, history, acreHistory } of settled) {
    byAbbv[abbv] = summary;
    summaries.push(...summary);
    currentPlantingsByAbbv[abbv] = plantings;
    currentPlantings.push(...plantings);
    if (history) yieldHistoryByAbbv[abbv] = history;
    if (acreHistory) acreHistoryByAbbv[abbv] = acreHistory;
    if (history || acreHistory) {
      const perf = summarizeCropPerformance(abbv, history ?? [], acreHistory ?? []);
      cropPerformanceByAbbv[abbv] = perf;
      cropPerformance.push(...perf);
    }
  }

  return {
    byAbbv,
    summaries,
    currentPlantingsByAbbv,
    currentPlantings,
    yieldHistoryByAbbv,
    acreHistoryByAbbv,
    cropPerformanceByAbbv,
    cropPerformance,
    unknownAbbvs
  };
};

/**
 * Helper — collapse a flat summary list into a single row per
 * `crop` + `yieldUOM`, summing acres and averaging non-null yields weighted
 * by acres. Useful for state-level rollups.
 */
export const dedupeSummariesByCrop = (
  rows: ReadonlyArray<AgronomyCropSummary>
): AgronomyCropSummary[] => {
  const map = new Map<string, AgronomyCropSummary & { _yieldAcres: number; _yieldSum: number }>();
  for (const row of rows) {
    const key = `${row.crop}::${row.yieldUOM ?? ''}`;
    const existing = map.get(key);
    const yieldAcres = row.avgYield != null ? row.totalAcres : 0;
    const yieldSum = row.avgYield != null ? row.avgYield * row.totalAcres : 0;
    if (!existing) {
      map.set(key, {
        ...row,
        meta: { ...(row.meta ?? {}) },
        _yieldAcres: yieldAcres,
        _yieldSum: yieldSum
      });
    } else {
      existing.totalAcres += row.totalAcres;
      existing._yieldAcres += yieldAcres;
      existing._yieldSum += yieldSum;
    }
  }
  return Array.from(map.values()).map(({ _yieldAcres, _yieldSum, ...rest }) => ({
    ...rest,
    avgYield: _yieldAcres > 0 ? _yieldSum / _yieldAcres : null
  }));
};

